/**
 * Clean video player pages for sources whose own player page carries scam ads (Cloudflare media Worker, /embed/<source>?id=<n>).
 *
 * Why: JAVAPI's JAV player page (server.apijav.com/?mvapm_embed=<id>) loads ad scripts from outside sites (benchform.org, borrowhourglass.com,
 * a CloudFront address, and more at runtime: portalfluently.com, eifitcannotke.org, zoologyfibre.com). They draw fake "your Windows is
 * infected" and fake "I'm not a robot" boxes over the video, and the first clicks on "Click to play" open pop-under tabs to ad sites
 * (seen in a real browser 2026-10-25). The app showed that page in an unrestricted iframe.
 *
 * The Worker fetches the same page, removes every script and frame from any other site plus the page's own obfuscated ad loader (an inline
 * script; the player is a separate, readable one), turns pop-up windows off, and serves it with a Content-Security-Policy that only lets
 * scripts come from the source itself and allows no frames, so ad code added later or loaded at runtime is blocked as well. It is served from
 * the Worker's own address, never from goonru.app, so the source's code can never reach the app's cookies or storage.
 *
 * The app's iframe is NOT sandboxed for this page on purpose: the player detects any sandbox (four checks, one of them a Chrome PDF plugin
 * probe that no sandbox setting passes) and refuses to play, to keep its pop-unders working. With the ad code gone there is nothing left for a
 * sandbox to stop, and the page cannot reach the app anyway.
 *
 * Pure functions plus the request handler with an injected fetch, so it is unit-tested (lib/player-embed.test.ts) without Cloudflare.
 */

export interface PlayerSource {
  /** The site the page and its own scripts come from. */
  origin: string
  /** What a video id looks like (a number for JAVAPI, a slug for Goontai). */
  idPattern: RegExp
  /** Where the player API answers; its `embed_url` is the page address (JAVAPI). Without it `pageUrl` builds the address. */
  api?: string
  pageUrl?: (id: string) => string
  /** Other sites whose scripts the page really needs (its libraries); anything else is removed and blocked. `*.x.com` covers subdomains. */
  scriptHosts?: string[]
  /** Other sites that may be framed (the video mirrors); the ad frames are removed and blocked. */
  frameHosts?: string[]
  /** Inline scripts to cut when their code matches (the source's ad code), besides every obfuscated one. */
  dropInline?: RegExp[]
  /** Parts of the page to cut (ad placeholders and the transparent layer over the video that catches the first click). */
  dropElements?: RegExp[]
  /**
   * Addresses the page checks with a cross-origin HEAD request that only the source's own site may make (its video servers answer CORS for
   * hentaiocean.com alone). Served from the Worker the page's own checks would fail, so they are answered by the Worker (/probe/<source>).
   */
  probePattern?: RegExp
}

/** Sources with a cleaned player. */
export const PLAYER_SOURCES: Record<string, PlayerSource> = {
  javapi: { origin: "https://server.apijav.com", api: "https://server.apijav.com/wp-json/myvideo/v1", idPattern: /^\d{1,12}$/ },
  // NOT javapi_hentai: its player page has no ad code (no outside script or frame on every page measured, 2026-10-25), and its videos are direct
  // files (hentaiapijav.cinevids.xyz) whose host refuses any page address but the source's own, so from the Worker most of them did not play
  // (13711 and 13709 failed there and play in the original). It uses the original page.
  // Goontai (hentaiocean.com): ExoClick's ad-provider.js, a 16 KB pop-under script (zone 5752838), an ad placeholder, and a transparent layer over
  // the video that swallows the first click for it; once running it adds a second ad frame (s.pemsrv.com, which loads a Chaturbate ad). Its own
  // libraries come from two CDNs and its video mirrors are *.hentaiocean.com frames (checked 2026-10-25).
  goontai: {
    origin: "https://hentaiocean.com",
    idPattern: /^[a-z0-9][a-z0-9_-]{0,120}$/i,
    pageUrl: (id) => `https://hentaiocean.com/embed/${id}`,
    scriptHosts: ["cdn.jsdelivr.net", "cdnjs.cloudflare.com"],
    frameHosts: ["*.hentaiocean.com"],
    dropInline: [/pemsrv|AdProvider|adConfig|popunder|click-layer|clickLayer/],
    dropElements: [/<ins\b[^>]*\bdata-zoneid[^>]*>\s*<\/ins>/gi, /<div\b[^>]*\bid\s*=\s*["']click-layer["'][^>]*>\s*<\/div>/gi],
    // The page looks for a second ("Universal") mirror, the one browsers without AV1 support need, by HEAD-requesting its video servers.
    probePattern: /^https:\/\/w\d{1,2}\.hentaiocean\.com\/video\/h264\//,
  },
}

/** A script that sends those checks to the Worker (same address as the page, so no cross-origin rule applies) and leaves every other request alone. */
export function probeShim(sourceId: string, pattern: RegExp): string {
  return (
    `<script>(function(){var f=window.fetch,p=${pattern.toString()};window.fetch=function(u,o){try{var s=String(u&&u.url||u);` +
    `if(p.test(s)&&(!o||!o.method||String(o.method).toUpperCase()==='HEAD')){return f.call(this,location.origin+'/probe/${sourceId}?url='+encodeURIComponent(s),{method:'HEAD',signal:o&&o.signal})}}catch(e){}` +
    `return f.apply(this,arguments)}})()</script>`
  )
}

/** GET|HEAD /probe/<source>?url=: whether the source's own video server has that file. Only addresses the source's recipe names are asked. */
export async function handlePlayerProbe(request: Request, fetchImpl: FetchLike = fetch as FetchLike): Promise<Response> {
  const url = new URL(request.url)
  const sourceId = url.pathname.replace(/^\/probe\//, "").replace(/\/+$/, "")
  const source = PLAYER_SOURCES[sourceId]
  const target = url.searchParams.get("url") ?? ""
  if (!source?.probePattern || !source.probePattern.test(target)) return new Response(null, { status: 403 })
  try {
    const res = await fetchImpl(target, { method: "HEAD", headers: { "User-Agent": UA, Origin: source.origin, Referer: `${source.origin}/` } })
    return new Response(null, { status: res.ok ? 200 : 404, headers: { "Cache-Control": "private, max-age=300" } })
  } catch {
    return new Response(null, { status: 502 })
  }
}

const hostMatches = (host: string, pattern: string) => (pattern.startsWith("*.") ? host.endsWith(pattern.slice(1)) : host === pattern)

const hostOf = (src: string, base: string): string | null => {
  try {
    return new URL(src.replace(/&amp;|&#038;/g, "&"), base).host
  } catch {
    return null
  }
}

/**
 * How machine-obfuscated a script is: the number of `name[12345]`-style lookups. The JAV page's inline ad loader scores about 420; the
 * readable players of both JAVAPI servers score 0 (measured 2026-10-25).
 */
export function obfuscationScore(code: string): number {
  return (code.match(/\b[\w$]{2,6}\[\d{4,}\]/g) ?? []).length
}

const OBFUSCATED = 50

/**
 * Removes every <script src> and <iframe> that points at another site than `origin`, and every inline script that is machine-obfuscated (the
 * ad loader). The readable inline player stays; the Content-Security-Policy stops it from loading outside code. Returns the cleaned page,
 * the outside hosts that were removed and how many obfuscated inline scripts were removed.
 */
export function stripForeignCode(
  html: string,
  origin: string,
  options: Pick<PlayerSource, "scriptHosts" | "frameHosts" | "dropInline" | "dropElements"> = {},
): { html: string; removed: string[]; obfuscated: number } {
  const own = new URL(origin).host
  const removed: string[] = []
  const foreign = (src: string, allowed: string[] = []) => {
    const host = hostOf(src, origin)
    if (host === null || host === own || allowed.some((pattern) => hostMatches(host, pattern))) return false
    removed.push(host)
    return true
  }
  let cleaned = html
    .replace(/<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>[\s\S]*?<\/script\s*>/gi, (tag, src: string) => (foreign(src, options.scriptHosts) ? "" : tag))
    .replace(/<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*\/>/gi, (tag, src: string) => (foreign(src, options.scriptHosts) ? "" : tag))
    .replace(/<iframe\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>[\s\S]*?<\/iframe\s*>/gi, (tag, src: string) => (foreign(src, options.frameHosts) ? "" : tag))
  for (const element of options.dropElements ?? []) cleaned = cleaned.replace(element, "")
  let obfuscated = 0
  const withoutLoader = cleaned.replace(/<script\b(?![^>]*\bsrc\s*=)[^>]*>([\s\S]*?)<\/script\s*>/gi, (tag, code: string) => {
    if (obfuscationScore(code) >= OBFUSCATED) {
      obfuscated++
      return ""
    }
    return (options.dropInline ?? []).some((pattern) => pattern.test(code)) ? "" : tag
  })
  return { html: withoutLoader, removed: [...new Set(removed)], obfuscated }
}

/** A first script that turns pop-up windows off (the player itself no longer opens any; only its ads did). */
const NO_POPUPS = "<script>try{Object.defineProperty(window,'open',{value:function(){return null},writable:false,configurable:false})}catch(e){}</script>"

/**
 * Puts <base href> first in <head>, so the page's relative addresses still point at the source after it is served from the Worker, followed
 * by the pop-up guard so it runs before any of the page's own scripts.
 */
export function withBase(html: string, origin: string, extraHead = ""): string {
  const base = `<base href="${origin}/">${NO_POPUPS}${extraHead}`
  return /<head\b[^>]*>/i.test(html) ? html.replace(/<head\b[^>]*>/i, (head) => head + base) : base + html
}

/**
 * Only the source may supply scripts, styles and fonts (plus Google Fonts, which the page uses); no frames at all, no forms, no plugins.
 * Pictures, the video stream and its requests may come from any https address, because the video is served from changing hosts.
 * `ancestors` are the sites allowed to show this page in a frame (ours).
 */
export function playerCsp(origin: string, ancestors: string[], scriptHosts: string[] = [], frameHosts: string[] = []): string {
  const hosts = (list: string[]) => list.map((h) => `https://${h}`).join(" ")
  return [
    "default-src 'none'",
    `script-src 'unsafe-inline' 'unsafe-eval' ${origin} ${hosts(scriptHosts)} blob:`.replace(/ {2,}/g, " "),
    `style-src 'unsafe-inline' ${origin} https://fonts.googleapis.com`,
    `font-src ${origin} https://fonts.gstatic.com data:`,
    "img-src https: data: blob:",
    "media-src https: data: blob:",
    "connect-src https: data: blob:",
    "worker-src blob:",
    // Only the browser's own PDF viewer (the player's sandbox check, see object-src); never a page from another site.
    `frame-src data: blob: chrome-extension: ${hosts(frameHosts)}`.trim(),
    "child-src blob:",
    // The player checks for a sandbox by loading a tiny data: PDF in an <object>; blocking it reads as "sandboxed" and it refuses to play.
    "object-src data:",
    "form-action 'none'",
    `base-uri ${origin}`,
    `frame-ancestors ${ancestors.length ? ancestors.join(" ") : "'none'"}`,
  ].join("; ")
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

const UA = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36"

const page = (status: number, text: string) =>
  new Response(`<!doctype html><meta charset="utf-8"><body style="margin:0;background:#000;color:#aaa;font:14px sans-serif;display:grid;place-items:center;height:100vh">${text}</body>`, {
    status,
    headers: { "Content-Type": "text/html; charset=utf-8", "Content-Security-Policy": "default-src 'none'; style-src 'unsafe-inline'" },
  })

/**
 * GET /embed/<source>?id=<post id>. `caller` is our site that asked (checked by the media Worker before this runs); it is sent on to the
 * source as the Referer, as the plain iframe did, and it is the only site allowed to frame the answer.
 */
export async function handlePlayerEmbed(request: Request, caller: string, ancestors: string[], fetchImpl: FetchLike = fetch as FetchLike): Promise<Response> {
  const url = new URL(request.url)
  const sourceId = url.pathname.replace(/^\/embed\//, "").replace(/\/+$/, "")
  const source = PLAYER_SOURCES[sourceId]
  if (!source) return page(404, "Unknown player")
  const id = url.searchParams.get("id") ?? ""
  if (!source.idPattern.test(id)) return page(400, "Bad video id")

  // The page address: from the player API (JAVAPI; the hentai server adds a token to it) or built from the id.
  let embedUrl: string
  if (source.api) {
    try {
      const res = await fetchImpl(`${source.api}/player/${id}`, { headers: { "User-Agent": UA, Accept: "application/json", Referer: `${source.origin}/` } })
      if (!res.ok) return page(res.status === 404 ? 404 : 502, "This video is not available")
      const data = (await res.json()) as { embed_url?: string }
      embedUrl = String(data.embed_url ?? "").replace(/&#038;|&amp;/g, "&")
    } catch {
      return page(502, "The video player could not be reached")
    }
  } else {
    embedUrl = source.pageUrl ? source.pageUrl(id) : ""
  }
  if (hostOf(embedUrl, source.origin) !== new URL(source.origin).host) return page(502, "Unexpected player address")

  let html: string
  try {
    const res = await fetchImpl(embedUrl, { headers: { "User-Agent": request.headers.get("user-agent") || UA, Accept: "text/html", Referer: `${caller}/` } })
    if (!res.ok) return page(502, "The video player could not be reached")
    html = await res.text()
  } catch {
    return page(502, "The video player could not be reached")
  }

  const { html: cleaned } = stripForeignCode(html, source.origin, source)
  return new Response(withBase(cleaned, source.origin, source.probePattern ? probeShim(sourceId, source.probePattern) : ""), {
    status: 200,
    headers: {
      "Content-Type": "text/html; charset=utf-8",
      "Content-Security-Policy": playerCsp(source.origin, ancestors, source.scriptHosts, source.frameHosts),
      "Cache-Control": "private, max-age=300",
      "X-Content-Type-Options": "nosniff",
      "Referrer-Policy": "strict-origin-when-cross-origin",
    },
  })
}
